import { cva, type VariantProps } from "class-variance-authority";
import { Slot } from "radix-ui";
import type * as React from "react";

import { type ButtonVariant as CrowButtonVariant, buttonLookClass } from "@crow/ui/button-class";
import { cn } from "../../lib/utils";

/**
 * The Crow charter's buttons (brand/crow-charte.html), drawn by the design system's
 * (`design-system/ui`, copied in `src/crow`): its colours, focus ring, disabled and pressed states.
 * Crowdex keeps its own names and sizes: `default` is the one filled button of a zone, in the
 * cobalt accent; `outline` is the bordered one, `secondary` sits on the control surface, `ghost`
 * and `link` are bare; `destructive` is red text and a red line, never a red fill. Heights follow
 * the density-aware control tokens (28/32/40 px at normal density).
 */
const CROW_VARIANT = {
	default: "primary",
	destructive: "danger",
	outline: "secondary",
	secondary: "ghost",
	ghost: "ghost",
	link: "ghost",
} as const satisfies Record<string, CrowButtonVariant>;

const buttonVariants_ = cva(
	"type-ui gap-2 rounded-control duration-ui aria-invalid:border-destructive [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4",
	{
		variants: {
			variant: {
				default: "",
				destructive: "",
				outline: "",
				secondary:
					"bg-secondary text-secondary-foreground hover:bg-secondary/80 hover:text-secondary-foreground",
				// Bare buttons stay bare when disabled: the design system fills disabled buttons.
				ghost: "disabled:bg-transparent",
				link: "rounded-none text-foreground underline-offset-4 hover:bg-transparent hover:underline disabled:bg-transparent",
			},
			size: {
				default: "h-control-md px-control-px-md py-2 has-[>svg]:px-control-px-sm",
				xs: "h-control-xs gap-1 px-control-px-xs type-caption has-[>svg]:px-1.5 [&_svg:not([class*='size-'])]:size-3",
				sm: "h-control-sm gap-1.5 px-control-px-sm has-[>svg]:px-2.5",
				lg: "type-body h-control-lg px-control-px-lg has-[>svg]:px-control-px-md",
				icon: "size-control-md",
				"icon-xs": "size-control-xs [&_svg:not([class*='size-'])]:size-3",
				"icon-sm": "size-control-sm",
				"icon-lg": "size-control-lg",
			},
		},
		defaultVariants: {
			variant: "default",
			size: "default",
		},
	},
);

type ButtonVariantProps = VariantProps<typeof buttonVariants_>;

/** A button's classes, for a link or a trigger that must stay its own element. */
function buttonVariants({
	variant,
	size,
	className,
}: ButtonVariantProps & { className?: string } = {}): string {
	return cn(
		buttonLookClass(CROW_VARIANT[variant ?? "default"]),
		buttonVariants_({ variant, size }),
		className,
	);
}

function Button({
	className,
	variant = "default",
	size = "default",
	asChild = false,
	...props
}: React.ComponentProps<"button"> &
	ButtonVariantProps & {
		asChild?: boolean;
	}) {
	const Comp = asChild ? Slot.Root : "button";

	return (
		<Comp
			data-slot="button"
			data-variant={variant}
			data-size={size}
			className={buttonVariants({ variant, size, className })}
			{...props}
		/>
	);
}

export { Button, buttonVariants };
